import { useMsal } from '@azure/msal-react';
import { faCalendar, faClone } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PrimaryButton, StatusButton } from 'component-library';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import { Config } from '../../config';
import { Dashboard } from '../../models/ApiModels';
import { GetDashboard, GetDashboardCompare } from '../../services/DashboardService';
import useRequest from '../../utils/net/useRequest';
import { launchCombinedConfetti, launchTargetConfetti } from './dashboardConfetti';
import DashboardConfettiControls from './DashboardConfettiControls';
import DashboardDataBoats from './DashboardDataBoats';
import DashboardDataCustomers from './DashboardDataCustomers';
import DashBoardDataLicenses from './DashBoardDataLicenses';
import DashboardDataOrders from './DashboardDataOrders';
import DashBoardDataOverview from './DashBoardDataOverview';
import DashboardDataProduction from './DashboardDataProduction';
import DashboardDataSoftware from './DashboardDataSoftware';
import DashboardDataSystems from './DashboardDataSystems';
import DashboardTargets from './DashboardTargets';
import { buildDashboardTargets, getFulfilledTargetKeys } from './dashboardTargetsUtils';
import DateComparisonDropDown from './DateComparisonDropDown';

type DateOption = {
  value: string;
  date: string;
};

const allTargetKeys = ['systems', 'boats', 'upgrades', 'ultimate'] as const;

function DashboardHome() {
  const dateOptionsOne = useMemo(
    () => [
      {
        value: 'today',
        date: moment().format('YYYY-MM-DD'),
      },
      {
        value: 'yesterday',
        date: moment().subtract(1, 'days').format('YYYY-MM-DD'),
      },
      {
        value: 'last week',
        date: moment().subtract(6, 'days').format('YYYY-MM-DD'),
      },
      {
        value: 'last month',
        date: moment().subtract(1, 'months').format('YYYY-MM-DD'),
      },
    ],
    []
  );

  const dateOptionsTwo = useMemo<DateOption[]>(
    () => [
      {
        value: 'yesterday',
        date: moment().subtract(1, 'days').format('YYYY-MM-DD'),
      },
      {
        value: 'last week',
        date: moment().subtract(6, 'days').format('YYYY-MM-DD'),
      },
      {
        value: 'last month',
        date: moment().subtract(1, 'months').format('YYYY-MM-DD'),
      },
    ],
    []
  );

  const [openCustomerSystemCountModal, setOpenSystemPerCustomerModal] = useState(false);
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [customStartDate, setCustomStartDate] = useState<Date>();
  const [customEndDate, setCustomEndDate] = useState<Date>();
  const isOutsideOfRange = useMemo(() => {
    if ((customEndDate && customEndDate > new Date()) || (customStartDate && customStartDate > new Date())) return true;
    else return false;
  }, [customStartDate, customEndDate]);

  const CustomDateStart = () => {
    return (
      <div>
        <ReactDatePicker
          popperPlacement='top'
          dateFormat={'yyyy-MM-dd'}
          placeholderText='Select date...'
          fixedHeight
          todayButton='Today'
          isClearable
          selected={customStartDate}
          onChange={(date: Date) => setCustomStartDate(date)}
          className='bg-gray-3 prose-paragraphSmaller cursor-pointer'
          customInput={
            <div className='bg-gray-3 w-full prose-paragraphSmaller cursor-pointer h-10 p-2 flex items-center gap-2'>
              <FontAwesomeIcon icon={faCalendar} className='opacity-20' />
              <input
                readOnly
                className='bg-gray-3 cursor-pointer'
                placeholder='Select date...'
                onChange={(e) => null}
                value={customStartDate ? moment(customStartDate).format('YYYY-MM-DD') : ''}
              />
            </div>
          }
        />
      </div>
    );
  };

  const CustomDateEnd = () => {
    return (
      <div>
        <ReactDatePicker
          popperPlacement='top'
          dateFormat={'yyyy-MM-dd'}
          placeholderText='Select date...'
          fixedHeight
          todayButton='Today'
          isClearable
          selected={customEndDate}
          onChange={(date: Date) => setCustomEndDate(date)}
          className='bg-gray-3 prose-paragraphSmaller cursor-pointer'
          customInput={
            <div className='bg-gray-3 w-full prose-paragraphSmaller cursor-pointer h-10 p-2 flex items-center gap-2'>
              <FontAwesomeIcon icon={faCalendar} className='opacity-20' />
              <input
                readOnly
                className='bg-gray-3 cursor-pointer'
                placeholder='Select date...'
                onChange={(e) => null}
                value={customEndDate ? moment(customEndDate).format('YYYY-MM-DD') : ''}
              />
            </div>
          }
        />
      </div>
    );
  };

  const [startDate, setStartDate] = useState(dateOptionsOne[0]);
  const [endDate, setEndDate] = useState(dateOptionsTwo[0]);
  const { accounts } = useMsal();
  const account = accounts[0];
  const [compare, setCompare] = useState(false);
  const [dashboardData, isDashboardDataLoading] = useRequest<Dashboard>(() => GetDashboard(), []);
  const [dashboardDataCompare, isDashboardDataCompareLoading] = useRequest<Dashboard>(
    () =>
      compare &&
      GetDashboardCompare(moment(startDate.date).format('YYYY-MM-DD'), moment(endDate.date).format('YYYY-MM-DD')),
    [startDate, endDate, compare]
  );
  const hasAutoConfettiFired = useRef(false);
  const activeDashboardData = compare ? dashboardDataCompare : dashboardData;
  const showConfettiButtons = Config.b2c_domain !== 'humphreecustomers';
  const fulfilledTargetKeys = useMemo(() => {
    if (!activeDashboardData) return [];
    return getFulfilledTargetKeys(buildDashboardTargets(activeDashboardData));
  }, [activeDashboardData]);

  const launchSystemsConfetti = useCallback(() => {
    void launchTargetConfetti('systems');
  }, []);

  const launchBoatsConfetti = useCallback(() => {
    void launchTargetConfetti('boats');
  }, []);

  const launchUpgradesConfetti = useCallback(() => {
    void launchTargetConfetti('upgrades');
  }, []);

  const launchUltimateConfetti = useCallback(() => {
    void launchTargetConfetti('ultimate');
  }, []);

  const launchAllConfetti = useCallback(() => {
    const keysToLaunch = fulfilledTargetKeys.length > 0 ? fulfilledTargetKeys : [...allTargetKeys];
    void launchCombinedConfetti(keysToLaunch);
  }, [fulfilledTargetKeys]);

  useEffect(() => {
    if (!activeDashboardData || hasAutoConfettiFired.current) return;
    if (fulfilledTargetKeys.length === 0) return;

    if (fulfilledTargetKeys.length > 1) {
      void launchCombinedConfetti(fulfilledTargetKeys);
    } else {
      void launchTargetConfetti(fulfilledTargetKeys[0]);
    }

    hasAutoConfettiFired.current = true;
  }, [activeDashboardData, fulfilledTargetKeys]);

  return (
    <div>
      {isDashboardDataLoading || isDashboardDataCompareLoading ? (
        <div className='flex items-center flex-col gap-4 h-96 justify-center'>
          <span className='text-primary-400 prose-heading5'>Loading dashboard...</span>
          <div className='w-12 h-12 border-l-2 border-primary-400 rounded-full animate-spin'></div>
        </div>
      ) : (
        <>
          <div className='header mb-10'>
            <div className='flex'>
              <div className='prose-heading3'>
                Welcome, <span className='text-primary-100'>{account?.name}</span>
              </div>
              {/* Disabled in prod until reports work properly again */}
              {Config.b2c_domain !== 'humphreecustomers' && (
                <div className='ml-auto'>
                  {!compare ? (
                    <PrimaryButton icon={faClone} label={'Compare'} onClick={() => setCompare(!compare)} />
                  ) : (
                    <div className='flex gap-2'>
                      <PrimaryButton label='Compare custom dates' onClick={() => setOpenDatePicker(true)} />
                      <StatusButton
                        icon={faClone}
                        label={'Stop comparing'}
                        onClick={() => setCompare(!compare)}
                        status={'error'}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
            {compare && (
              <>
                <div className='flex gap-2'>
                  Total data
                  <DateComparisonDropDown
                    value={startDate.value}
                    options={dateOptionsOne}
                    setDate={(date) => setStartDate(date)}
                  />
                  compared with
                  <DateComparisonDropDown
                    value={endDate.value}
                    options={dateOptionsTwo}
                    setDate={(date) => setEndDate(date)}
                  />
                </div>
                {openDatePicker && (
                  <div className='fixed z-50 top-0 left-0 w-full h-full'>
                    <div className='fixed w-screen h-screen bottom-0 bg-black opacity-50 overlay'></div>
                    <div className='flex items-center justify-center h-full'>
                      <div className='relative flex flex-col gap-4 m-2 p-4 md:p-8 bg-white max-h-[90%] w-full md:max-w-xl shadow-popup popup'>
                        <span className='prose-heading4'>Pick custom comparison dates</span>
                        <div className='flex gap-2 my-8'>
                          <span>
                            Start date:
                            <CustomDateStart />
                          </span>
                          <span>
                            End date:
                            <CustomDateEnd />
                          </span>
                        </div>
                        <div className='flex gap-2'>
                          <StatusButton
                            label='Close'
                            status={'error'}
                            onClick={() => {
                              setOpenDatePicker(false);
                              setCustomEndDate(undefined);
                              setCustomStartDate(undefined);
                            }}
                          />
                          <PrimaryButton
                            label={'Compare dates'}
                            disabled={isOutsideOfRange || !customStartDate || !customEndDate}
                            onClick={() => {
                              setStartDate({
                                value: moment(customStartDate).format('YYYY-MM-DD'),
                                date: moment(customStartDate).format('YYYY-MM-DD'),
                              });
                              setEndDate({
                                value: moment(customEndDate).format('YYYY-MM-DD'),
                                date: moment(customEndDate).format('YYYY-MM-DD'),
                              });
                              setOpenDatePicker(false);
                            }}
                          />
                        </div>
                        {isOutsideOfRange && (
                          <div className=' mt-4 text-error-200 prose-labelStandard'>Dates are outside of range</div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
            <span className='prose-paragraphBase'>The dashboard is updated daily for you.</span>
          </div>
          {dashboardData && !compare && (
            <div>
              <DashBoardDataOverview dashboardData={dashboardData} compare={compare} />
              <DashboardTargets dashboardData={dashboardData} compare={compare} />
              <DashboardDataSystems dashboardData={dashboardData} compare={compare} />
              <DashBoardDataLicenses dashboardData={dashboardData} compare={compare} />
              <div className='flex flex-wrap gap-4'>
                <DashboardDataBoats dashboardData={dashboardData} compare={compare} />
                <DashboardDataCustomers
                  dashboardData={dashboardData}
                  compare={compare}
                  startDate={startDate}
                  endDate={endDate}
                  openSystemPerCustomerModal={openCustomerSystemCountModal}
                  setOpenSystemPerCustomerModal={setOpenSystemPerCustomerModal}
                />
              </div>
              <DashboardDataOrders dashboardData={dashboardData} compare={compare} />
              {/* Disabled in prod until reports work properly again */}
              {Config.b2c_domain !== 'humphreecustomers' && (
                <DashboardDataSoftware dashboardData={dashboardData} compare={compare} />
              )}
              <DashboardDataProduction dashboardData={dashboardData} compare={compare} />
              {showConfettiButtons && (
                <DashboardConfettiControls
                  onLaunchSystems={launchSystemsConfetti}
                  onLaunchBoats={launchBoatsConfetti}
                  onLaunchUpgrades={launchUpgradesConfetti}
                  onLaunchUltimate={launchUltimateConfetti}
                  onLaunchCombined={launchAllConfetti}
                />
              )}
            </div>
          )}
          {dashboardDataCompare && compare && (
            <div>
              <DashBoardDataOverview dashboardData={dashboardDataCompare} compare={compare} />
              <DashboardTargets dashboardData={dashboardDataCompare} compare={compare} />
              <DashboardDataSystems dashboardData={dashboardDataCompare} compare={compare} />
              <DashBoardDataLicenses dashboardData={dashboardDataCompare} compare={compare} />
              <div className='flex flex-wrap gap-4'>
                <DashboardDataBoats dashboardData={dashboardDataCompare} compare={compare} />
                <DashboardDataCustomers
                  dashboardData={dashboardDataCompare}
                  compare={compare}
                  startDate={startDate}
                  endDate={endDate}
                  openSystemPerCustomerModal={openCustomerSystemCountModal}
                  setOpenSystemPerCustomerModal={setOpenSystemPerCustomerModal}
                />
              </div>
              <DashboardDataOrders dashboardData={dashboardDataCompare} compare={compare} />
              <DashboardDataSoftware dashboardData={dashboardDataCompare} compare={compare} />
              <DashboardDataProduction dashboardData={dashboardDataCompare} compare={compare} />
              {showConfettiButtons && (
                <DashboardConfettiControls
                  onLaunchSystems={launchSystemsConfetti}
                  onLaunchBoats={launchBoatsConfetti}
                  onLaunchUpgrades={launchUpgradesConfetti}
                  onLaunchUltimate={launchUltimateConfetti}
                  onLaunchCombined={launchAllConfetti}
                />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default DashboardHome;
